import ACTIONS from "./action";
import { tableColumns } from "../constant/tableColumns";
import {
  defaultFilters,
  defaultFiltersInputs,
  defaultSavedListFilters,
  defaultSavedListSorting
} from "../constant/filters";
import { defaultPatientState } from "../constant/patient";
import commonReducers from "@tailormed/common-client/store/reducer";
import { selectedViews } from "../constant/table";
import { RequiredSignerTypesBySignerType, ApplicationFormSignatureStatuses } from "../constant/ESign";

/**
 * Helper function to remove items from table data by ID field
 * @param {Object} state - Current Redux state
 * @param {string} selectedView - View identifier
 * @param {Array} idsToRemove - Array of IDs to remove
 * @param {string} idFieldName - Name of the ID field (e.g., 'patientId', 'applicationId', 'claimId')
 * @returns {Object} New state with filtered items
 */
const removeItemsFromTable = (state, selectedView, idsToRemove, idFieldName) => {
  const existingData = state.tablesData[selectedView];
  if (!existingData || !existingData.patients || !idsToRemove?.length) {
    return state;
  }

  const idsSet = new Set(idsToRemove);
  const filteredItems = existingData.patients.filter((item) => !idsSet.has(item[idFieldName]));

  return {
    ...state,
    tablesData: {
      ...state.tablesData,
      [selectedView]: {
        ...existingData,
        patients: filteredItems,
        totalPatients: Math.max(
          0,
          (existingData.totalPatients || 0) - (existingData.patients.length - filteredItems.length)
        )
      }
    }
  };
};

const defaultState = {
  idpConfig: undefined,
  oktaClientId: undefined,

  environment: undefined,
  tier: undefined,
  isPhiAllowed: undefined,
  user: undefined,
  users: [],
  settings: {},
  selectedAssistanceProgramById: undefined,
  showAssistanceProgramModal: false,
  showExpressEnrollModal: false,
  showSupport: {
    visible: false,
    assistancePrograms: undefined
  },
  showSecureUpload: {
    visible: false
  },
  currentApplication: undefined,
  assistancePrograms: [],
  assignees: [],
  userGroups: [],
  userGroupsWithUsers: [],
  networkActions: {},
  tableColumns,
  filters: defaultFilters,
  filtersInputs: defaultFiltersInputs,
  searchedPatients: [],
  selectedView: undefined,
  providers: {},
  doctorDuplicates: [],
  isDoctorExactDuplicate: false,
  physicianListFilters: {
    searchTerm: "",
    showInactive: false,
    currentPage: 1,
    sortColumn: null,
    sortOrder: null
  },
  tablesData: {},
  savedListFilters: {
    query: defaultSavedListFilters.query,
    sortingStatuses: defaultSavedListSorting,
    filterBy: defaultSavedListFilters.filterBy
  },
  formPopups: {
    visible: false,
    contentKey: null,
    isDuplicateMode: false
  },
  units: [],
  unknownAndInactiveInsuranceInfo: [],
  visitType: [],
  filterList: {},
  savedListRecordCounts: {},
  filterListStatuses: {
    id: undefined,
    isModified: false,
    selectedPage: 1,
    sorter: {},
    canModify: false,
    showDiscardModal: false,
    nextSelectedView: "",
    isDiscardModalVisible: false
  },
  isSavedListinitialized: false,
  drugs: [],
  summaryCounters: {},
  patient: defaultPatientState,
  oopEstimations: [],
  formItemsDiagnoses: [],
  APSponsors: [],
  applicationClaims: {
    claims: []
  },
  filterByClaimsStatus: "",
  isCopyPastePopoverOpen: false,
  isExpressEnrollFormOpen: false,
  enrollment: {
    isUpdatingFile: false,
    applicationFormSignatures: null,
    pdfMetaData: {},
    remoteRequestErrorMessage: {},
    loadingWidgets: {},
    isApplicationFormModalVisible: false,
    isApplicationFormModalAutoOpened: false
  },
  badgeCounts: { notifications: 0, tasks: 0, patientTasks: { total: 0, patientId: "" }, extraCare: 0 },
  notificationDisplayFilter: "unread",
  isSidebarCollapsed: false,
  shouldReturnToRelevantPatient: { shouldReturn: false, notificationId: null },
  enrollmentNotes: [],
  enrollmentNoteAddedSuccess: false,
  lastSelfEnrollInvitation: null,
  currentApplicationDetailsFields: {},
  searchResultPage: {
    wasSearchResultPage: false,
    searchBy: null,
    searchText: ""
  },
  previousPage: {},
  aiChatBot: {
    isIntroPanelShown: true
  },
  patientBnPinDrugs: [],
  floatingChat: {
    isVisible: false,
    isMinimized: false,
    activeConversationId: null,
    isExpanded: false,
    conversationHistory: {}, // Map of conversationId -> history
    shouldLoadHistory: false,
    isStreaming: false
  },
  bulkSelection: {
    // Map of viewKey -> { selectedRecordIds: number[], mode: 'manual' | 'all' }
  },
  bulkActionLoading: false,
  bulkActionResult: null,
  bulkActionNavigatedAway: false,
  ...commonReducers.defaultState
};

const reducer = (state = defaultState, action) => {
  switch (action.type) {
    case ACTIONS.Types.SET_IDP_CONFIG: {
      const { idpConfig } = action;
      return { ...state, idpConfig };
    }
    case ACTIONS.Types.SET_OKTA_CONFIG: {
      const { oktaClientId } = action;
      return { ...state, oktaClientId };
    }
    case ACTIONS.Types.LOGOUT: {
      return { ...defaultState };
    }

    case ACTIONS.Types.SET_ENVIRONMENT: {
      const { environment } = action;
      return { ...state, environment };
    }

    case ACTIONS.Types.SET_TIER: {
      const { tier } = action;
      return { ...state, tier };
    }

    case ACTIONS.Types.SET_USER: {
      const { user } = action;
      return { ...state, user };
    }

    case ACTIONS.Types.SET_USERS: {
      const { users } = action;
      return { ...state, users };
    }

    case ACTIONS.Types.SET_ASSIGNEES: {
      const { assignees } = action;
      return { ...state, assignees };
    }

    case ACTIONS.Types.INIT_SAVED_LIST: {
      return { ...state, isSavedListinitialized: true };
    }

    case ACTIONS.Types.SET_USER_GROUPS: {
      const { userGroups } = action;
      return { ...state, userGroups };
    }

    case ACTIONS.Types.SET_USER_GROUPS_WITH_USERS: {
      const { userGroupsWithUsers } = action;
      return { ...state, userGroupsWithUsers };
    }

    case ACTIONS.Types.SET_IS_PHI_ALLOWED: {
      const { isPhiAllowed } = action;
      return { ...state, isPhiAllowed };
    }

    case ACTIONS.Types.SET_IS_SIDE_BAR_COLLAPSED: {
      const { isSidebarCollapsed } = action;
      return { ...state, isSidebarCollapsed };
    }

    case ACTIONS.Types.SET_SHOULD_RETURN_TO_RELEVANT_PATIENT: {
      const { shouldReturn, notificationId } = action;
      return { ...state, shouldReturnToRelevantPatient: { shouldReturn, notificationId } };
    }

    case ACTIONS.Types.SET_SELECTED_VIEW: {
      const { selectedView } = action;
      return { ...state, selectedView };
    }

    case ACTIONS.Types.UPDATE_ENROLLMENT_NOTES: {
      const { enrollmentNotes } = action;
      return { ...state, enrollmentNotes };
    }

    case ACTIONS.Types.ENROLLMENT_NOTE_ADDED_SUCCESS: {
      return { ...state, enrollmentNoteAddedSuccess: true };
    }

    case ACTIONS.Types.RESET_ENROLLMENT_NOTE_SUCCESS: {
      return { ...state, enrollmentNoteAddedSuccess: false };
    }

    case ACTIONS.Types.UPDATE_FILTER_LIST_STATUSES: {
      const { filterListID, canModify, showDiscardModal, nextSelectedView } = action;
      return {
        ...state,
        filterListStatuses: {
          ...state.filterListStatuses,
          id: filterListID,
          canModify,
          showDiscardModal,
          nextSelectedView
        }
      };
    }

    case ACTIONS.Types.SET_FILTER_LIST_DISCARD_MODAL_VISIBLE: {
      return {
        ...state,
        filterListStatuses: {
          ...state.filterListStatuses,
          isDiscardModalVisible: action.visible
        }
      };
    }

    case ACTIONS.Types.MARK_SAVED_FILTER_AS_CHANGED: {
      const { filterListID, filterListStatus } = action;
      return {
        ...state,
        filterListStatuses: {
          ...state.filterListStatuses,
          id: filterListID,
          isModified: filterListStatus
        }
      };
    }

    case ACTIONS.Types.UPDATE_SAVED_FILTER_VIEW_PARAMETERS: {
      const { filterListID, selectedPage = 1, sorter = {} } = action;
      return {
        ...state,
        filterListStatuses: {
          ...state.filterListStatuses,
          id: filterListID,
          selectedPage,
          sorter
        }
      };
    }

    case ACTIONS.Types.CLEAR_SORTER: {
      const { view } = action;
      return {
        ...state,
        tablesData: {
          ...state.tablesData,
          [view]: { ...state.tablesData[view], sorter: {} }
        }
      };
    }

    case ACTIONS.Types.CLEAR_SELECTED_VIEW: {
      return { ...state, selectedView: undefined };
    }

    case ACTIONS.Types.SET_OOP_ESTIMATIONS: {
      const { data } = action;
      return { ...state, oopEstimations: data };
    }

    case ACTIONS.Types.SET_FORM_ITEMS_DIAGNOSES: {
      const { data } = action;
      return { ...state, formItemsDiagnoses: data };
    }

    case ACTIONS.Types.SET_ASSISTANCE_PROGRAMS: {
      const { assistancePrograms } = action;
      return { ...state, assistancePrograms };
    }

    case ACTIONS.Types.SET_SHOW_ASSISTANCE_PROGRAM_MODAL: {
      const { showAssistanceProgramModal } = action;
      return { ...state, showAssistanceProgramModal };
    }

    case ACTIONS.Types.SET_SHOW_EXPRESS_ENROLL_MODAL: {
      const { showExpressEnrollModal } = action;
      return { ...state, showExpressEnrollModal };
    }

    case ACTIONS.Types.SET_SHOW_SUPPORT: {
      const { visible, assistanceProgram } = action;
      return { ...state, showSupport: { ...state.showSupport, visible, assistanceProgram } };
    }

    case ACTIONS.Types.SET_SHOW_SECURE_UPLOAD: {
      const { visible } = action;
      return { ...state, showSecureUpload: { ...state.showSecureUpload, visible } };
    }

    case ACTIONS.Types.SET_SELECTED_ASSISTANCE_PROGRAM_BY_ID: {
      const { assistanceProgram } = action;
      return { ...state, selectedAssistanceProgramById: assistanceProgram };
    }

    case ACTIONS.Types.CLEAR_SELECTED_ASSISTANCE_PROGRAM_BY_ID: {
      return { ...state, selectedAssistanceProgramById: undefined };
    }

    case ACTIONS.Types.CLEAR_ASSISTANCE_PROGRAMS: {
      return { ...state, assistancePrograms: [] };
    }

    case ACTIONS.Types.SET_NETWORK_ACTION: {
      const { actionType, isFetching, msg, code } = action;
      return { ...state, networkActions: { ...state.networkActions, [actionType]: { isFetching, msg, code } } };
    }

    case ACTIONS.Types.SET_TABLE_COLUMNS: {
      const { tableColumns } = action;
      return { ...state, tableColumns };
    }

    case ACTIONS.Types.SET_SEARCHED_PATIENTS: {
      const { searchedPatients, selectedPage, sorter } = action;

      return { ...state, searchedPatients: { ...searchedPatients, selectedPage, sorter } };
    }

    case ACTIONS.Types.UPDATE_SEARCHED_PATIENT: {
      const { patientId, sharedFields } = action;
      let searchedPatientIndex = state.searchedPatients?.patients?.findIndex(
        ({ patientId: id }) => parseInt(id) == patientId
      );
      if (searchedPatientIndex >= 0) {
        let searchedPatients = {
          ...state.searchedPatients,
          patients: Object.assign([...state.searchedPatients.patients], {
            [searchedPatientIndex]: { ...state.searchedPatients.patients[searchedPatientIndex], ...sharedFields }
          })
        };
        return { ...state, searchedPatients };
      }
      return state;
    }

    case ACTIONS.Types.CLEAR_SEARCHED_PATIENTS: {
      return { ...state, searchedPatients: [] };
    }

    case ACTIONS.Types.SET_FILTERS: {
      const { filters } = action;
      return { ...state, filters };
    }

    case ACTIONS.Types.CLEAR_FILTERS: {
      return { ...state, filters: defaultFilters };
    }

    case ACTIONS.Types.SET_FILTERS_INPUTS: {
      const { filterName, value } = action;
      return { ...state, filtersInputs: { ...state.filtersInputs, [filterName]: value } };
    }

    case ACTIONS.Types.SET_MULTIPLE_FILTERS_INPUTS: {
      const { filters } = action;
      return { ...state, filtersInputs: { ...defaultFiltersInputs, ...filters } };
    }

    case ACTIONS.Types.CLEAR_FILTERS_INPUTS: {
      return { ...state, filtersInputs: defaultFiltersInputs };
    }

    case ACTIONS.Types.RESET_SAVED_LIST_VIEW_FILTERS: {
      return {
        ...state,
        savedListFilters: { ...defaultSavedListFilters, sortingStatuses: defaultSavedListSorting }
      };
    }

    case ACTIONS.Types.SET_TABLES_DATA: {
      const { tableData, selectedPage, selectedView, sorter } = action;

      return {
        ...state,
        tablesData: {
          ...state.tablesData,
          [selectedView]: { ...tableData, selectedPage: selectedPage, sorter }
        }
      };
    }

    case ACTIONS.Types.UPDATE_PATIENTS_IN_PLACE: {
      const { selectedView, patients: updatedPatients } = action;

      // Get existing data for the view
      const existingData = state.tablesData[selectedView];
      if (!existingData || !existingData.patients || !updatedPatients?.length) {
        return state;
      }

      // Create a map of updated patients by patientId for O(1) lookup
      const updatedPatientsMap = new Map(updatedPatients.map((p) => [p.patientId, p]));

      // Replace matching patients in the existing array
      const mergedPatients = existingData.patients.map((patient) => {
        const updated = updatedPatientsMap.get(patient.patientId);
        return updated || patient;
      });

      return {
        ...state,
        tablesData: {
          ...state.tablesData,
          [selectedView]: {
            ...existingData,
            patients: mergedPatients
          }
        }
      };
    }

    case ACTIONS.Types.UPDATE_ENROLLMENTS_IN_PLACE: {
      const { selectedView, enrollments: updatedEnrollments } = action;

      const existingData = state.tablesData[selectedView];
      if (!existingData || !existingData.patients || !updatedEnrollments?.length) {
        return state;
      }

      const updatedEnrollmentsMap = new Map(updatedEnrollments.map((e) => [e.applicationId, e]));

      const mergedPatients = existingData.patients.map((enrollment) => {
        const updated = updatedEnrollmentsMap.get(enrollment.applicationId);
        return updated || enrollment;
      });

      return {
        ...state,
        tablesData: {
          ...state.tablesData,
          [selectedView]: {
            ...existingData,
            patients: mergedPatients
          }
        }
      };
    }

    case ACTIONS.Types.UPDATE_ENROLLMENTS_FOR_PATIENT_IN_PLACE: {
      const { selectedView, patches } = action;

      const existingData = state.tablesData[selectedView];
      if (!existingData || !existingData.patients || !patches?.length) {
        return state;
      }

      const patchByPatientId = new Map(patches.map((p) => [p.patientId, p]));

      const mergedPatients = existingData.patients.map((enrollment) => {
        const patch = patchByPatientId.get(enrollment.patientId);
        return patch ? { ...enrollment, ...patch } : enrollment;
      });

      return {
        ...state,
        tablesData: {
          ...state.tablesData,
          [selectedView]: {
            ...existingData,
            patients: mergedPatients
          }
        }
      };
    }

    case ACTIONS.Types.UPDATE_CLAIMS_IN_PLACE: {
      const { selectedView, claims: updatedClaims } = action;

      const existingData = state.tablesData[selectedView];
      if (!existingData || !existingData.patients || !updatedClaims?.length) {
        return state;
      }

      const updatedClaimsMap = new Map(updatedClaims.map((c) => [c.claimId, c]));

      const mergedPatients = existingData.patients.map((claim) => {
        const updated = updatedClaimsMap.get(claim.claimId);
        return updated || claim;
      });

      return {
        ...state,
        tablesData: {
          ...state.tablesData,
          [selectedView]: {
            ...existingData,
            patients: mergedPatients
          }
        }
      };
    }

    case ACTIONS.Types.UPDATE_CLAIMS_FOR_PATIENT_IN_PLACE: {
      const { selectedView, patches } = action;

      const existingData = state.tablesData[selectedView];
      if (!existingData || !existingData.patients || !patches?.length) {
        return state;
      }

      const patchByPatientId = new Map(patches.map((p) => [p.patientId, p]));

      const mergedPatients = existingData.patients.map((claim) => {
        const patch = patchByPatientId.get(claim.patientId);
        return patch ? { ...claim, ...patch } : claim;
      });

      return {
        ...state,
        tablesData: {
          ...state.tablesData,
          [selectedView]: {
            ...existingData,
            patients: mergedPatients
          }
        }
      };
    }

    case ACTIONS.Types.REMOVE_PATIENTS_FROM_TABLE: {
      const { selectedView, patientIds } = action;
      return removeItemsFromTable(state, selectedView, patientIds, "patientId");
    }

    case ACTIONS.Types.REMOVE_ENROLLMENTS_FROM_TABLE: {
      const { selectedView, enrollmentIds } = action;
      return removeItemsFromTable(state, selectedView, enrollmentIds, "applicationId");
    }

    case ACTIONS.Types.REMOVE_CLAIMS_FROM_TABLE: {
      const { selectedView, claimIds } = action;
      return removeItemsFromTable(state, selectedView, claimIds, "claimId");
    }

    case ACTIONS.Types.CLEAR_TABLES_DATA: {
      const { selectedView } = action;
      return {
        ...state,
        tablesData: {
          ...state.tablesData,
          [selectedView]: {
            ...state.tablesData[selectedView],
            patients: [],
            totalPatients: 0,
            selectedPage: 1,
            sorter: {}
          }
        }
      };
    }

    case ACTIONS.Types.SET_PATIENT_TABLE_RECORD_DATA: {
      const { patientId, sharedFields } = action;
      const newTableData = {};
      Object.entries({ ...state.tablesData }).forEach(([tableKey, { patients, ...rest }]) => {
        newTableData[tableKey] = {
          ...rest,
          patients: patients
            ? patients.map((record) =>
                record.patientId === patientId ? { ...record, ...sharedFields } : { ...record }
              )
            : undefined
        };
      });
      return {
        ...state,
        tablesData: newTableData
      };
    }

    case ACTIONS.Types.SET_SAVED_LISTS_TABLE_RECORD_DATA: {
      const { recordData } = action;
      const savedListKey = selectedViews.savedList;

      const savedListRecords = [...state.tablesData[savedListKey].data];
      const listIndex = savedListRecords.findIndex((record) => record.id === recordData.id);
      savedListRecords[listIndex] = recordData;

      return {
        ...state,
        tablesData: {
          ...state.tablesData,
          [savedListKey]: { ...state.tablesData[savedListKey], data: savedListRecords }
        }
      };
    }

    case ACTIONS.Types.SET_SAVED_LIST_RECORD_COUNT: {
      const { listId, count } = action;
      return {
        ...state,
        savedListRecordCounts: {
          ...state.savedListRecordCounts,
          [listId]: count
        }
      };
    }

    case ACTIONS.Types.SET_SETTINGS: {
      const { settings } = action;
      const settingsObject = {};
      settings.map((setting) => (settingsObject[setting.key] = setting));
      return { ...state, settings: settingsObject };
    }

    case ACTIONS.Types.SET_SELECTED_FILTER_LIST_TYPE: {
      const { listType } = action;
      return {
        ...state,
        savedListFilters: {
          ...state.savedListFilters,
          filterBy: listType
        }
      };
    }

    case ACTIONS.Types.SET_LIST_NAME_QUERY_ACTION: {
      const { query } = action;
      return {
        ...state,
        savedListFilters: {
          ...state.savedListFilters,
          query
        }
      };
    }

    case ACTIONS.Types.SET_SAVED_LIST_SORT_BY_ACTION: {
      const { sortBy } = action;
      const { filterBy, sortingStatuses } = state.savedListFilters;
      const tabColumns = sortingStatuses[filterBy];
      return {
        ...state,
        savedListFilters: {
          ...state.savedListFilters,
          sortingStatuses: {
            ...sortingStatuses,
            [filterBy]: { ...tabColumns, [tabColumns.orderBy]: sortBy }
          }
        }
      };
    }

    case ACTIONS.Types.SET_SAVED_LIST_ORDER_BY_ACTION: {
      const { orderBy } = action;
      const { filterBy, sortingStatuses } = state.savedListFilters;
      const tabColumns = sortingStatuses[filterBy];

      return {
        ...state,
        savedListFilters: {
          ...state.savedListFilters,
          sortingStatuses: {
            ...sortingStatuses,
            [filterBy]: { ...tabColumns, orderBy }
          }
        }
      };
    }

    case ACTIONS.Types.PATIENT_DETAILS: {
      const { patientData } = action;
      return {
        ...state,
        patient: {
          ...state.patient,
          details: patientData
        }
      };
    }

    case ACTIONS.Types.UPDATE_PATIENT_CONSENT_EVENT: {
      const { latestConsentEvent } = action;

      if (!state.patient?.details || !Array.isArray(state.patient.details)) {
        return state;
      }

      const updatedDetails = state.patient.details.map((detail, index) =>
        index === 0 ? { ...detail, latestConsentEvent } : detail
      );

      return {
        ...state,
        patient: {
          ...state.patient,
          details: updatedDetails
        }
      };
    }

    case ACTIONS.Types.CLEAR_PATIENT: {
      return {
        ...state,
        patient: defaultPatientState
      };
    }

    case ACTIONS.Types.SET_PATIENT_FOUND: {
      const { isFound } = action;
      return { ...state, patient: { ...state.patient, isFound } };
    }

    case ACTIONS.Types.PROVIDERS: {
      const { providerType, providerData } = action;
      return { ...state, providers: { ...state.providers, [providerType]: providerData } };
    }

    case ACTIONS.Types.SET_DOCTOR_DUPLICATES: {
      const { duplicates } = action;
      return { ...state, doctorDuplicates: duplicates };
    }

    case ACTIONS.Types.SET_DOCTOR_EXACT_DUPLICATE: {
      const { isExact } = action;
      return { ...state, isDoctorExactDuplicate: isExact };
    }

    case ACTIONS.Types.SET_PHYSICIAN_LIST_FILTERS: {
      const { filters } = action;
      return { ...state, physicianListFilters: { ...state.physicianListFilters, ...filters } };
    }

    case ACTIONS.Types.GET_AP_SPONSORS: {
      const { data } = action;
      return { ...state, APSponsors: data };
    }

    case ACTIONS.Types.POTENTIAL_SAVINGS: {
      const { potentialSavings } = action;
      return { ...state, patient: { ...state.patient, potentialSavings: potentialSavings } };
    }

    case ACTIONS.Types.SET_CURRENT_APPLICATION: {
      const { currentApplication } = action;
      return { ...state, currentApplication };
    }

    case ACTIONS.Types.SET_COPY_PASTE_POPOVER: {
      const { isCopyPastePopoverOpen } = action;
      return { ...state, isCopyPastePopoverOpen };
    }

    case ACTIONS.Types.SET_EXPRESS_ENROLL_FORM_OPEN: {
      const { isExpressEnrollFormOpen } = action;
      return { ...state, isExpressEnrollFormOpen };
    }

    case ACTIONS.Types.SET_UNITS: {
      const { units } = action;
      return { ...state, units: units };
    }

    case ACTIONS.Types.SET_FILTER_LIST: {
      const { filterList } = action;
      return { ...state, filterList: filterList };
    }

    case ACTIONS.Types.SET_PATIENT_UNKNOWN_AND_INACTIVE_INSURANCE: {
      const { unknownAndInactiveInsuranceInfo } = action;
      return { ...state, unknownAndInactiveInsuranceInfo: unknownAndInactiveInsuranceInfo };
    }

    case ACTIONS.Types.SET_VISITS_TYPES: {
      const { visitType } = action;
      return { ...state, visitType: visitType };
    }

    case ACTIONS.Types.SET_FORM_POPUPS: {
      const { visible, contentKey, isDuplicateMode } = action;
      return { ...state, formPopups: { visible, contentKey, isDuplicateMode } };
    }

    case ACTIONS.Types.SET_APPLICATION_CLAIMS: {
      const { claims } = action;
      return { ...state, applicationClaims: { claims } };
    }

    case ACTIONS.Types.SET_FILTER_BY_CLAIMS_STATUS: {
      const { filterByClaimsStatus } = action;
      return { ...state, filterByClaimsStatus };
    }

    case ACTIONS.Types.CLEAR_APPLICATION_CLAIMS: {
      return {
        ...state,
        applicationClaims: defaultState.applicationClaims,
        filterByClaimsStatus: defaultState.filterByClaimsStatus
      };
    }

    case ACTIONS.Types.SET_SUMMARY_COUNTERS: {
      const { summaryCounters } = action;
      return { ...state, summaryCounters: summaryCounters };
    }

    case ACTIONS.Types.SET_CURRENT_APPLICATION_DETAILS_FIELDS: {
      const { currentApplicationDetailsFields } = action;
      return { ...state, currentApplicationDetailsFields };
    }

    case ACTIONS.Types.PATIENT_JOURNEY: {
      const { journeyData } = action;
      return { ...state, patient: { ...state.patient, journey: journeyData } };
    }

    case ACTIONS.Types.GENERIC_SERVICES: {
      const { genericServices } = action;
      return {
        ...state,
        patient: {
          ...state.patient,
          genericServices: genericServices
        }
      };
    }

    case ACTIONS.Types.PATIENT_OPPORTUNITY: {
      const { data } = action;
      return { ...state, patient: { ...state.patient, opportunity: data } };
    }

    case ACTIONS.Types.POTENTIAL_SAVINGS_JOURNEY: {
      const { data } = action;
      return { ...state, patient: { ...state.patient, potentialSavingsJourney: data } };
    }

    case ACTIONS.Types.SET_LAST_SELF_ENROLL_INVITATION: {
      const { patientId, invitation } = action;
      return { ...state, lastSelfEnrollInvitation: { patientId, invitation } };
    }

    case ACTIONS.Types.SET_SELECTED_JOURNEY_EVENT_HANDLER: {
      const { selectedJourneyEvent } = action;
      return { ...state, patient: { ...state.patient, selectedJourneyEvent: selectedJourneyEvent } };
    }

    case ACTIONS.Types.PATIENT_COVERAGES: {
      const { coverages } = action;
      return { ...state, patient: { ...state.patient, coverages } };
    }

    case ACTIONS.Types.POLICIES: {
      const { policies } = action;
      return { ...state, patient: { ...state.patient, policies } };
    }

    case ACTIONS.Types.PATIENT_SHIPPING_OPTIONS: {
      const { shippingOptions } = action;
      return { ...state, patient: { ...state.patient, shippingOptions: shippingOptions } };
    }

    case ACTIONS.Types.POTENTIAL_SAVING: {
      const { papData } = action;
      const updatePotentialSavings = state.patient.potentialSavings.map((p) => {
        if (p.id === papData.id) {
          return papData;
        } else {
          return p;
        }
      });
      return {
        ...state,
        patient: {
          ...state.patient,
          potentialSavings: updatePotentialSavings
        }
      };
    }

    case ACTIONS.Types.UPDATE_APPLICATION_RELEVANCE: {
      const { papId, isIrrelevantForRenewal } = action;
      const indexOfRequestedPs = state.patient.potentialSavings.findIndex(
        (potentialSaving) => potentialSaving.id === papId
      );
      const updatedPotentialSaving = [...state.patient.potentialSavings];
      updatedPotentialSaving[indexOfRequestedPs].isIrrelevantForRenewal = isIrrelevantForRenewal;

      return { ...state, patient: { ...state.patient, potentialSavings: updatedPotentialSaving } };
    }

    case ACTIONS.Types.SET_APPLICATION_FORM: {
      const { applicationForm } = action;
      return { ...state, patient: { ...state.patient, applicationForm } };
    }

    case ACTIONS.Types.SET_EXPRESS_ENROLLMENT_CONFIGURATION: {
      const { expressEnrollmentConfiguration } = action;
      return { ...state, patient: { ...state.patient, expressEnrollmentConfiguration } };
    }

    case ACTIONS.Types.SET_APPLICATION_ATTACHMENTS: {
      const { applicationAttachments } = action;
      return { ...state, patient: { ...state.patient, applicationAttachments } };
    }

    case ACTIONS.Types.UPDATE_ENROLLMENT_STORE: {
      const { key, value } = action;
      // Deep-merge for object keys that may be updated concurrently
      if (["loadingWidgets", "remoteRequestErrorMessage"].includes(key)) {
        return {
          ...state,
          enrollment: {
            ...state.enrollment,
            [key]: { ...(state.enrollment?.[key] || {}), ...value }
          }
        };
      }
      return { ...state, enrollment: { ...state.enrollment, [key]: value } };
    }

    case ACTIONS.Types.RESET_ENROLLMENT_STORE: {
      return { ...state, enrollment: { ...defaultState.enrollment } };
    }

    case ACTIONS.Types.SET_BADGE_COUNTS: {
      const { key, value } = action;
      return { ...state, badgeCounts: { ...state.badgeCounts, [key]: value } };
    }

    case ACTIONS.Types.SET_NOTIFICATION_DISPLAY_FILTER: {
      const { filter } = action;
      return { ...state, notificationDisplayFilter: filter };
    }

    case ACTIONS.Types.RESET_APPLICATION_FORM_SIGNATURE: {
      const { signerType } = action;
      const currentApplicationFormSignatures = state.enrollment.applicationFormSignatures;

      const notUpdatedSignatures = currentApplicationFormSignatures.filter(
        (signature) => signature.signerType !== signerType
      );

      let currSigToUpdate = currentApplicationFormSignatures.find((signature) => signature.signerType === signerType);

      currSigToUpdate = {
        ...currSigToUpdate,
        status: ApplicationFormSignatureStatuses.NOT_STARTED,
        type: null,
        signedBy: null,
        signerType: null,
        phiRemoteSignatureRequestId: null,
        phiRemoteSignatureRequest: null
      };
      const updatedSignatures = [...notUpdatedSignatures, currSigToUpdate];
      return { ...state, enrollment: { ...state.enrollment, applicationFormSignatures: updatedSignatures } };
    }

    case ACTIONS.Types.SET_APPLICATION_FORM_SIGNATURE: {
      const { signerType, applicationFormSignature } = action;
      const currentApplicationFormSignatures = state.enrollment.applicationFormSignatures;
      let relevantRequiredSignerTypes = RequiredSignerTypesBySignerType[signerType] || [];

      const notUpdatedSignatures = currentApplicationFormSignatures.filter(
        (signature) => !relevantRequiredSignerTypes.includes(signature.requiredSignerType)
      );

      const updatedSignatures = [...notUpdatedSignatures, applicationFormSignature];

      return { ...state, enrollment: { ...state.enrollment, applicationFormSignatures: updatedSignatures } };
    }

    case ACTIONS.Types.SET_APPLICATION_FORM_MODAL_VISIBILITY: {
      const { isVisible, isAutoOpened = false } = action;
      return {
        ...state,
        enrollment: {
          ...state.enrollment,
          isApplicationFormModalVisible: isVisible,
          isApplicationFormModalAutoOpened: isVisible ? isAutoOpened : false
        }
      };
    }

    case ACTIONS.Types.SET_SEARCH_RESULT_PAGE: {
      const { wasSearchResultPage, searchBy, searchText } = action;
      return { ...state, searchResultPage: { wasSearchResultPage, searchBy, searchText } };
    }

    case ACTIONS.Types.SET_RECENT_PATIENT_PAGE: {
      const { wasRecentPatientPage } = action;
      return { ...state, recentPatientPage: { wasRecentPatientPage } };
    }

    case ACTIONS.Types.SET_PREVIOUS_PAGE: {
      const { previousPage } = action;
      return { ...state, previousPage: { ...previousPage } };
    }

    case ACTIONS.Types.SET_AI_CHAT_BOT_INTRO_PANEL_SHOWN: {
      const { isShown } = action;
      return { ...state, aiChatBot: { ...state.aiChatBot, isIntroPanelShown: isShown } };
    }

    case ACTIONS.Types.SET_PATIENT_BN_PIN_DRUGS: {
      const { patientBnPinDrugs } = action;
      return { ...state, patientBnPinDrugs };
    }
    case ACTIONS.Types.SET_FLOATING_CHAT_VISIBLE: {
      const { isVisible } = action;
      return { ...state, floatingChat: { ...state.floatingChat, isVisible } };
    }

    case ACTIONS.Types.SET_FLOATING_CHAT_CONVERSATION_ID: {
      const { conversationId } = action;
      return { ...state, floatingChat: { ...state.floatingChat, activeConversationId: conversationId } };
    }

    case ACTIONS.Types.SET_FLOATING_CHAT_MINIMIZED: {
      const { isMinimized } = action;
      return { ...state, floatingChat: { ...state.floatingChat, isMinimized } };
    }

    case ACTIONS.Types.SET_FLOATING_CHAT_SHOULD_LOAD_HISTORY: {
      const { shouldLoadHistory } = action;
      return { ...state, floatingChat: { ...state.floatingChat, shouldLoadHistory } };
    }

    case ACTIONS.Types.SET_CHAT_STREAMING: {
      const { isStreaming } = action;
      return { ...state, floatingChat: { ...state.floatingChat, isStreaming } };
    }

    case ACTIONS.Types.CLEAR_FLOATING_CHAT_STATE: {
      return {
        ...state,
        floatingChat: {
          isVisible: false,
          isMinimized: false,
          activeConversationId: null,
          isExpanded: false,
          conversationHistory: {},
          shouldLoadHistory: false,
          isStreaming: false
        }
      };
    }

    case ACTIONS.Types.SET_FLOATING_CHAT_HISTORY: {
      const { conversationId, history } = action;
      return {
        ...state,
        floatingChat: {
          ...state.floatingChat,
          conversationHistory: {
            ...state.floatingChat.conversationHistory,
            [conversationId]: history
          }
        }
      };
    }

    case ACTIONS.Types.SET_BULK_SELECTION: {
      const { viewKey, selectedRecordIds } = action;
      return {
        ...state,
        bulkSelection: {
          ...state.bulkSelection,
          [viewKey]: {
            ...state.bulkSelection[viewKey],
            selectedRecordIds,
            mode: "manual"
          }
        }
      };
    }

    case ACTIONS.Types.ADD_TO_BULK_SELECTION: {
      const { viewKey, recordId } = action;
      const currentSelection = state.bulkSelection[viewKey]?.selectedRecordIds || [];
      if (currentSelection.includes(recordId)) {
        return state;
      }
      return {
        ...state,
        bulkSelection: {
          ...state.bulkSelection,
          [viewKey]: {
            ...state.bulkSelection[viewKey],
            selectedRecordIds: [...currentSelection, recordId],
            mode: "manual"
          }
        }
      };
    }

    case ACTIONS.Types.REMOVE_FROM_BULK_SELECTION: {
      const { viewKey, recordId } = action;
      const currentSelection = state.bulkSelection[viewKey]?.selectedRecordIds || [];
      return {
        ...state,
        bulkSelection: {
          ...state.bulkSelection,
          [viewKey]: {
            ...state.bulkSelection[viewKey],
            selectedRecordIds: currentSelection.filter((id) => id !== recordId),
            mode: "manual"
          }
        }
      };
    }

    case ACTIONS.Types.CLEAR_BULK_SELECTION: {
      const { viewKey } = action;
      return {
        ...state,
        bulkSelection: {
          ...state.bulkSelection,
          [viewKey]: {
            selectedRecordIds: [],
            mode: "manual"
          }
        }
      };
    }

    case ACTIONS.Types.SET_BULK_SELECTION_MODE: {
      const { viewKey, mode } = action;
      return {
        ...state,
        bulkSelection: {
          ...state.bulkSelection,
          [viewKey]: {
            ...state.bulkSelection[viewKey],
            mode
          }
        }
      };
    }

    case ACTIONS.Types.SET_BULK_ACTION_LOADING: {
      const { isLoading } = action;
      return { ...state, bulkActionLoading: isLoading };
    }

    case ACTIONS.Types.SET_BULK_ACTION_RESULT: {
      const { result } = action;
      return { ...state, bulkActionResult: result };
    }

    case ACTIONS.Types.CLEAR_BULK_ACTION_RESULT: {
      return { ...state, bulkActionResult: null };
    }

    case ACTIONS.Types.SET_BULK_ACTION_NAVIGATED_AWAY: {
      const { hasNavigatedAway } = action;
      return { ...state, bulkActionNavigatedAway: hasNavigatedAway };
    }

    case ACTIONS.Types.SET_PATIENT_NOTES: {
      const { notes } = action;
      return {
        ...state,
        patient: {
          ...state.patient,
          notes
        }
      };
    }

    case ACTIONS.Types.SET_PATIENT_ATTACHMENTS_COUNT: {
      const { count } = action;
      return {
        ...state,
        patient: {
          ...state.patient,
          attachmentsCount: count
        }
      };
    }

    default: {
      return commonReducers.reducer(state, action);
    }
  }
};

export { defaultState };
export default reducer;
